import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DashboardShell } from "@/components/DashboardShell";
import { PARTNER_NAV } from "@/lib/nav";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import {
  computeTotal,
  fetchInvoices,
  formatMoney,
  INVOICE_TYPES,
  nextInvoiceNumber,
  PAYMENT_METHODS,
  type InvoiceType,
} from "@/lib/invoices";
import { SERVICE_LABEL, useMyServices, type ServiceType } from "@/lib/services";

export const Route = createFileRoute("/_authenticated/partner/invoices/new")({
  head: () => ({
    meta: [
      { title: "New invoice · Landoo Partner" },
      {
        name: "description",
        content: "Create a customer invoice for a property or parking service.",
      },
    ],
  }),
  component: NewInvoicePage,
});

const inp =
  "w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/30";

function NewInvoicePage() {
  const { user, profile } = useAuth();
  const nav = useNavigate();
  const { activeServices } = useMyServices();
  const [serviceType, setServiceType] = useState<ServiceType | "">("");
  const [submitting, setSubmitting] = useState(false);
  const [listings, setListings] = useState<
    { id: string; label: string; kind: "property" | "parking" }[]
  >([]);
  const [f, setF] = useState({
    invoice_number: "",
    invoice_type: "custom" as InvoiceType,
    invoice_date: new Date().toISOString().slice(0, 10),
    due_date: "",
    business_name: "",
    business_address: "",
    business_phone: "",
    customer_name: "",
    customer_mobile: "",
    customer_email: "",
    customer_address: "",
    listing: "",
    description: "",
    quantity: "1",
    rate: "",
    tax_percent: "0",
    discount: "0",
    payment_method: "",
    notes: "",
    terms: "Payment due within 7 days of the invoice date.",
    signature_name: "",
  });
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    if (!user) return;
    void (async () => {
      const [inv, props, parks] = await Promise.all([
        fetchInvoices(user.id).catch(() => []),
        supabase.from("properties").select("id,title").eq("owner_id", user.id),
        supabase.from("parking_listings").select("id,title").eq("owner_id", user.id),
      ]);
      setF((s) => ({
        ...s,
        invoice_number: nextInvoiceNumber(inv.map((i) => i.invoice_number)),
        business_name: s.business_name || profile?.full_name || "",
        business_phone: s.business_phone || profile?.phone || "",
        business_address: s.business_address || profile?.city || "",
      }));
      setListings([
        ...((props.data as { id: string; title: string }[]) ?? []).map((p) => ({
          id: `property:${p.id}`,
          label: `Property · ${p.title}`,
          kind: "property" as const,
        })),
        ...((parks.data as { id: string; title: string }[]) ?? []).map((p) => ({
          id: `parking:${p.id}`,
          label: `Parking · ${p.title}`,
          kind: "parking" as const,
        })),
      ]);
    })();
  }, [user?.id, profile?.full_name]);

  const total = computeTotal(
    Number(f.quantity),
    Number(f.rate),
    Number(f.tax_percent),
    Number(f.discount),
  );

  const save = async (status: "draft" | "sent") => {
    if (!user) return;
    if (!f.customer_name) return toast.error("Customer name is required");
    if (!serviceType) return toast.error("Select one of your active services");
    if (!activeServices.includes(serviceType))
      return toast.error("Invoices can only be created under an active service");
    setSubmitting(true);
    const [kind, id] = f.listing ? f.listing.split(":") : [null, null];
    const { data, error } = await supabase
      .from("partner_invoices")
      .insert({
        owner_id: user.id,
        invoice_number: f.invoice_number,
        invoice_type: f.invoice_type,
        service_type: serviceType,
        invoice_date: f.invoice_date,
        due_date: f.due_date || null,
        business_name: f.business_name || null,
        business_address: f.business_address || null,
        business_phone: f.business_phone || null,
        customer_name: f.customer_name,
        customer_mobile: f.customer_mobile || null,
        customer_email: f.customer_email || null,
        customer_address: f.customer_address || null,
        property_id: kind === "property" ? id : null,
        parking_id: kind === "parking" ? id : null,
        description: f.description || null,
        quantity: Number(f.quantity) || 1,
        rate: Number(f.rate) || 0,
        tax_percent: Number(f.tax_percent) || 0,
        discount: Number(f.discount) || 0,
        total,
        payment_status: status,
        payment_method: f.payment_method || null,
        notes: f.notes || null,
        terms: f.terms || null,
        signature_name: f.signature_name || null,
      })
      .select("id")
      .maybeSingle();
    setSubmitting(false);
    if (error) return toast.error(error.message);
    toast.success(status === "sent" ? "Invoice marked as sent" : "Draft invoice saved");
    const newId = (data as { id: string } | null)?.id;
    if (newId) nav({ to: "/partner/invoices/$invoiceId", params: { invoiceId: newId } });
    else nav({ to: "/partner/invoices" });
  };

  return (
    <DashboardShell title="New invoice" role="Partner" items={PARTNER_NAV}>
      <form
        className="mx-auto max-w-3xl space-y-4 rounded-2xl border border-border bg-card p-5 shadow-soft"
        onSubmit={(e) => {
          e.preventDefault();
          save("draft");
        }}
      >
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Invoice number">
            <input
              className={inp}
              value={f.invoice_number}
              onChange={(e) => set("invoice_number", e.target.value)}
            />
          </Field>
          <Field label="Invoice date">
            <input
              type="date"
              className={inp}
              value={f.invoice_date}
              onChange={(e) => set("invoice_date", e.target.value)}
            />
          </Field>
          <Field label="Due date">
            <input
              type="date"
              className={inp}
              value={f.due_date}
              onChange={(e) => set("due_date", e.target.value)}
            />
          </Field>
        </div>

        <Field label="Service (active services only)">
          {activeServices.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-3 text-xs text-muted-foreground">
              No active service. Invoices cannot be created under inactive services.
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {activeServices.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setServiceType(s)}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
                    serviceType === s
                      ? "bg-brown text-primary-foreground"
                      : "bg-secondary text-brown-dark"
                  }`}
                >
                  {SERVICE_LABEL[s]}
                </button>
              ))}
            </div>
          )}
        </Field>

        <Field label="Invoice type">
          <select
            className={inp}
            value={f.invoice_type}
            onChange={(e) => set("invoice_type", e.target.value as InvoiceType)}
          >
            {INVOICE_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </Field>

        <Section>Partner business</Section>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Business name">
            <input
              className={inp}
              value={f.business_name}
              onChange={(e) => set("business_name", e.target.value)}
            />
          </Field>
          <Field label="Business phone">
            <input
              className={inp}
              value={f.business_phone}
              onChange={(e) => set("business_phone", e.target.value)}
            />
          </Field>
          <Field label="Business address">
            <input
              className={inp}
              value={f.business_address}
              onChange={(e) => set("business_address", e.target.value)}
            />
          </Field>
        </div>

        <Section>Customer</Section>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Customer name">
            <input
              required
              className={inp}
              value={f.customer_name}
              onChange={(e) => set("customer_name", e.target.value)}
            />
          </Field>
          <Field label="Customer mobile">
            <input
              className={inp}
              value={f.customer_mobile}
              onChange={(e) => set("customer_mobile", e.target.value)}
            />
          </Field>
          <Field label="Customer email">
            <input
              type="email"
              className={inp}
              value={f.customer_email}
              onChange={(e) => set("customer_email", e.target.value)}
            />
          </Field>
          <Field label="Customer address">
            <input
              className={inp}
              value={f.customer_address}
              onChange={(e) => set("customer_address", e.target.value)}
            />
          </Field>
        </div>

        <Section>Service</Section>
        <Field label="Related property or parking listing">
          <select
            className={inp}
            value={f.listing}
            onChange={(e) => set("listing", e.target.value)}
          >
            <option value="">Not linked</option>
            {listings.map((l) => (
              <option key={l.id} value={l.id}>
                {l.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Service description">
          <textarea
            rows={3}
            className={inp}
            value={f.description}
            onChange={(e) => set("description", e.target.value)}
          />
        </Field>
        <div className="grid gap-3 sm:grid-cols-4">
          <Field label="Quantity">
            <input
              type="number"
              step="any"
              className={inp}
              value={f.quantity}
              onChange={(e) => set("quantity", e.target.value)}
            />
          </Field>
          <Field label="Rate (₹)">
            <input
              type="number"
              step="any"
              className={inp}
              value={f.rate}
              onChange={(e) => set("rate", e.target.value)}
            />
          </Field>
          <Field label="Tax % (placeholder)">
            <input
              type="number"
              step="any"
              className={inp}
              value={f.tax_percent}
              onChange={(e) => set("tax_percent", e.target.value)}
            />
          </Field>
          <Field label="Discount (₹)">
            <input
              type="number"
              step="any"
              className={inp}
              value={f.discount}
              onChange={(e) => set("discount", e.target.value)}
            />
          </Field>
        </div>

        <div className="flex items-center justify-between rounded-xl bg-brand-soft px-4 py-3">
          <span className="text-xs font-semibold uppercase tracking-wider text-brown-dark">
            Total
          </span>
          <span className="text-lg font-bold text-brown-dark">{formatMoney(total)}</span>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Payment method">
            <select
              className={inp}
              value={f.payment_method}
              onChange={(e) => set("payment_method", e.target.value)}
            >
              <option value="">Not specified</option>
              {PAYMENT_METHODS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Signature (name)">
            <input
              className={inp}
              value={f.signature_name}
              onChange={(e) => set("signature_name", e.target.value)}
              placeholder="Authorised signatory"
            />
          </Field>
        </div>

        <Field label="Notes">
          <textarea
            rows={2}
            className={inp}
            value={f.notes}
            onChange={(e) => set("notes", e.target.value)}
          />
        </Field>
        <Field label="Terms">
          <textarea
            rows={2}
            className={inp}
            value={f.terms}
            onChange={(e) => set("terms", e.target.value)}
          />
        </Field>

        <div className="rounded-xl border border-dashed border-border bg-cream/50 p-3 text-xs text-muted-foreground">
          Payment gateway integration is not enabled. Payment status is recorded manually.
        </div>

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={submitting}
            className="flex-1 rounded-xl border border-border bg-background py-2.5 text-sm font-semibold text-brown-dark disabled:opacity-50"
          >
            Save draft
          </button>
          <button
            type="button"
            onClick={() => save("sent")}
            disabled={submitting}
            className="flex-1 rounded-xl bg-coral py-2.5 text-sm font-semibold text-white shadow-soft disabled:opacity-50"
          >
            {submitting ? "Saving…" : "Create & mark as sent"}
          </button>
        </div>
      </form>
    </DashboardShell>
  );
}

function Section({ children }: { children: React.ReactNode }) {
  return (
    <div className="border-t border-border pt-3 text-xs font-bold uppercase tracking-wider text-brown-dark">
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      {children}
    </label>
  );
}
